import React, { PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Heading } from '@swyftx/aviary/atoms/Typography';

import { cn } from '@shared/utils/lib/ui';

import { useAvo } from '@hooks/Avo/useAvo';
import { onboardingMachine } from '@routes/Onboarding/Onboarding.machine';
import { OnboardingStepId } from '@routes/Onboarding/types/Onboarding.types';

import { useOnboardingSelector, useOnboardingService } from '@Onboarding/Onboarding.context';

import { NavigationURLs } from 'src/lib/navigation/types';

type Props = {
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  submitDisabled?: boolean;
  hideTitle?: boolean;
  customTitle?: string;
  customFooterLeftElement?: React.ReactElement;
};

const OnboardingStepContainer: React.FC<PropsWithChildren<Props>> = (props) => {
  const { children, onSubmit, hideTitle, customFooterLeftElement, submitDisabled, customTitle } = props;
  const { t } = useTranslation('onboarding');
  const avo = useAvo();

  const onboardingService = useOnboardingService();

  const stepId = useOnboardingSelector((state) => {
    const [rootStateId, activeStateId] = state.toStrings();

    if (activeStateId != null) {
      const activeStateNode = onboardingMachine.getStateNodeByPath(activeStateId);
      if (activeStateNode.meta?.step && !activeStateNode.meta?.hidden) {
        return activeStateId as OnboardingStepId;
      }
    }

    return rootStateId as OnboardingStepId;
  });

  const isCompleted = useOnboardingSelector((state) => state.context.completedSteps[stepId]);

  useEffect(() => {
    if (!isCompleted) {
      avo.onboardingWizardStepStarted({
        screen: NavigationURLs.Onboarding,
        stepId,
      });
    }
  }, [avo, stepId, isCompleted]);

  const canGoBack = useOnboardingSelector((state) => state.can('BACK'));
  const canContinue = useOnboardingSelector((state) => state.can('NEXT'));
  const hideContinueButton = useOnboardingSelector((state) => state?.meta?.[stepId]?.hideContinueButton) ?? false;

  const [, setSearchParams] = useSearchParams();

  // Search params used purely for analytics & tracking purposes
  useEffect(() => {
    setSearchParams({ step: stepId });
  }, [stepId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBack = () => {
    onboardingService.send('BACK');
    avo.onboardingWizardBackClicked({
      screen: NavigationURLs.Onboarding,
      stepId,
    });
  };

  const onSubmitDefault = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onboardingService.send('NEXT');
  };

  const shouldRender = customFooterLeftElement !== undefined || canGoBack || (canContinue && !hideContinueButton);

  return (
    <form className='flex h-full flex-col' onSubmit={onSubmit ?? onSubmitDefault}>
      <FlexLayout direction='column'>
        {!hideTitle && (
          <Heading className='mb-8 font-[401]' size='h3'>
            {customTitle ?? t(`steps.${stepId}.title` as any)}
          </Heading>
        )}

        {children}
      </FlexLayout>

      {shouldRender && (
        <FlexLayout justifyContent='end' className='mt-48 flex-col gap-12 sm:flex-row sm:gap-0'>
          {customFooterLeftElement}
          {canGoBack && (
            <Button variant='outlined' size='lg' onClick={handleBack}>
              {t(`steps.${stepId}.backActionLabel` as any, t('actions.back'))}
            </Button>
          )}
          {canContinue && !hideContinueButton && (
            <Button
              type='submit'
              size='lg'
              className={cn('order-first ml-0 sm:order-last sm:ml-8', !canGoBack ? 'w-full' : '')}
              disabled={submitDisabled}
            >
              {t('actions.continue')}
            </Button>
          )}
        </FlexLayout>
      )}
    </form>
  );
};

export { OnboardingStepContainer };
